import { useCallback, useMemo, useState } from 'react';

// List one representative field per required‑device set – cheap test
const DEVICE_FIELD = 'Type'; // any required device-field works here

/* ------------------------------------------------------------------ */
/*  helpers                                                           */
/* ------------------------------------------------------------------ */
export function isAccessoriesArray(val) {
  return Array.isArray(val);
}

/** True when a concrete device exists **exactly** at `pathArr`        */
function hasDeviceAtPath(devs, pathArr = []) {
  return devs.some(d => {
    // pathArr.every((seg, idx) => d[`cat_${idx + 1}`] === seg) &&
    // !d[`cat_${pathArr.length + 1}`] &&           // nothing deeper
    // !!d.type                                     // real device payload

    const matchPath = pathArr.every((seg, idx) => d[`cat_${idx + 1}`] === seg);
    const nextCat = d[`cat_${pathArr.length + 1}`];
    const hasNoRealDeeperCats = !nextCat || isAccessoriesArray(nextCat);
    return matchPath && hasNoRealDeeperCats && !!(d.Type || d.type);
  });
}

export default function useMillerTree(devices = []) {
  const [path, setPath] = useState([]);   // current selection
  const [localBranches, setBranches]  = useState([]);   // ← new

  /* distinct values -------------------------------------------------- */
  const distinct = useCallback((level, basePath = path) => {
    const out = new Set();
    [...devices, ...localBranches].forEach(d => {
      for (let i = 0; i < level; i++) {
        const seg = basePath[i];
        if (seg && d[`cat_${i + 1}`] !== seg) return;
      }
      const val = d[`cat_${level + 1}`];
      // if (val) out.add(val);
      if (val && !isAccessoriesArray(val)) out.add(val);
    });
    return [...out].sort();
 }, [devices, path, localBranches]);

  /* branch colour ---------------------------------------------------- */
  const branchStatus = useCallback((level, value) => {
    let live = 0, total = 0;
    [...devices, ...localBranches].forEach(d => {
      if (
        path.slice(0, level).every((v, i) => d[`cat_${i + 1}`] === v) &&
        d[`cat_${level + 1}`] === value
      ) {
        total++;
        if (d.Live) live++;
      }
    });
    if (live === 0)         return 'empty';
    if (live === total)     return 'good';
    return 'mixed';
  }, [devices, path, localBranches]);

  /* identify leaf ---------------------------------------------------- */
  const isLeaf = useCallback((level, value) => {
    const fullPath = [...path.slice(0, level), value];
    // if there are child values -> not a leaf
    if (distinct(level + 1, fullPath).length > 0) return false;
    // it's a leaf **only** if at least one document at this path has DEVICE_FIELD
    return devices.some(d =>
      fullPath.every((v, i) => d[`cat_${i + 1}`] === v) &&
      // d[DEVICE_FIELD]
      (d.Type || d.type)
    );
 }, [distinct, devices, path, localBranches]);


  /* build columns ---------------------------------------------------- */
  const columns = useMemo(() => {
    const cols = [];
    for (let lvl = 0; lvl < 10; lvl++) {
      const vals = distinct(lvl);
      if (!vals.length) break;
      cols.push(vals);
      if (!path[lvl]) break;   // stop until user selects deeper level
    }
    // If the user stopped on a *branch* with no children, offer one
    // spare column.  But suppress it when the node is already a
    // concrete device – devices are leaf-only.
    const onDeviceLeaf = hasDeviceAtPath(devices, path);   // ← new
    if (!onDeviceLeaf &&
        path.length === cols.length &&
        cols.length < 10) {
      cols.push([]);             // empty column → +Branch / +Device
    }

    return cols;
 }, [distinct, path, localBranches, devices]);   // added `devices`

  /* public API ------------------------------------------------------- */
  const select = useCallback((lvl, val) => {
    setPath(prev => [...prev.slice(0, lvl), val]);
  }, []);


  /* add a branch at a specific column level */
  const addBranch = useCallback((label, level) => {
    const basePath = path.slice(0, level);    // truncate to that column
    const newPath  = [...basePath, label];

    /* ── leaf-only rule: cannot branch beneath an existing device ── */
    if (hasDeviceAtPath(devices, basePath)) {
      console.warn('Blocked: node already holds a device');
      return;                                 // silently abort
    }

    // avoid duplicates
    const dup =
      localBranches.some(b => newPath.every((v,i)=>b[`cat_${i+1}`]===v)) ||
      devices.some(d      => newPath.every((v,i)=>d[`cat_${i+1}`]===v));
    if (dup) return;

    const stub = {};
    newPath.forEach((v,i)=>{ stub[`cat_${i+1}`]=v; });
    setBranches(b => [...b, stub]);
    setPath(newPath);                         // select the new branch
 }, [path, localBranches, devices]);

  return { columns, path, select, branchStatus, isLeaf, addBranch, clearBranches: () => setBranches([]) };
}
